import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  selectActiveProperty,
  selectPropertyDateFormats
} from '@hkm/components/Menu/PropertySelector/domain/selectors';
import { GuestServiceStatusType } from '@hkm/shared/enum/guestServiceStatusType';
import { useEnabledGuestServiceStatuses } from '@hkm/shared/hooks/useEnabledGuestServiceStatuses';
import {
  createLocalTimeFromTzTime,
  createTimeWithTimezone
} from '@hkm/utils/dateHelper';
import dayjs from 'dayjs';

import { DictionaryEntity } from '@ac/library-api';
import {
  AcButton,
  AcButtonPattern
} from '@ac/mobile-components/dist/components/button';
import { AcButtonContent } from '@ac/mobile-components/dist/components/button-content';
import {
  AcFieldTime,
  defaultOutputFormat
} from '@ac/mobile-components/dist/components/field';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import { AcFormElement } from '@ac/mobile-components/dist/components/form-element';
import {
  AcModal,
  AcModalBody,
  AcModalFooter,
  AcModalHeader
} from '@ac/mobile-components/dist/components/modal';
import { AcRadioButton } from '@ac/mobile-components/dist/components/radio-button';
import { AcText } from '@ac/mobile-components/dist/components/text';
import {
  AlignItems,
  FlexDirection,
  Icon,
  JustifyContent,
  TextSize
} from '@ac/mobile-components/dist/enums';
import { Testable } from '@ac/mobile-components/dist/interfaces/componentProps';
import { formatTestSelector } from '@ac/mobile-components/dist/utils';

import './GuestServiceModal.css';

export interface GuestServiceModalProps extends Testable {
  open: boolean;
  description?: string;
  serviceDeferredUntil?: string;
  selectedGuestService?: GuestServiceStatusType;
  excludeGuestService?: GuestServiceStatusType[];

  onClose(): void;
  onConfirm(guestServiceStatus: GuestServiceStatusType, endTime?: string): void;
}

const HOUR_OFFSET = 1;

// tslint:disable:jsx-no-lambda
const GuestServiceModal: FC<GuestServiceModalProps> = props => {
  const { t } = useTranslation();

  const prefix = 'guest-service-modal';
  const formats = useSelector(selectPropertyDateFormats);
  const property = useSelector(selectActiveProperty);
  const enabledGuestServiceStatuses = useEnabledGuestServiceStatuses(
    props.excludeGuestService
  );
  const [selectedDeferredEndTime, setSelectedDeferredEndTime] = useState<
    string
  >();
  const [selectedGuestServiceStatus, setSelectedGuestServiceStatus] = useState<
    GuestServiceStatusType
  >();

  const onItemClick = (status: GuestServiceStatusType) => {
    if (status !== selectedGuestServiceStatus) {
      setSelectedGuestServiceStatus(status);
    }
  };

  const onConfirm = useCallback(() => {
    const timeDeferred = createTimeWithTimezone(
      selectedDeferredEndTime!,
      property.timeZoneCode
    ).format(defaultOutputFormat);
    const untilTime =
      selectedGuestServiceStatus !== GuestServiceStatusType.ServiceDeferred
        ? timeDeferred
        : selectedDeferredEndTime
        ? timeDeferred
        : undefined;
    props.onConfirm(selectedGuestServiceStatus!, untilTime);
    props.onClose();
  }, [selectedGuestServiceStatus, selectedDeferredEndTime]);

  const onFocus = useCallback(() => {
    // IOS hack: otherwise date won't change
    if (!selectedDeferredEndTime || !dayjs(selectedDeferredEndTime).isValid()) {
      const date = dayjs()
        .add(HOUR_OFFSET, 'hour')
        .format(defaultOutputFormat);
      setSelectedDeferredEndTime(date.toString());
    }
  }, [selectedDeferredEndTime]);

  const isServiceDeferredUntilValid = useMemo(() => {
    return selectedGuestServiceStatus ===
      GuestServiceStatusType.ServiceDeferred && !!selectedDeferredEndTime
      ? dayjs(selectedDeferredEndTime).isAfter(new Date())
      : true;
  }, [selectedDeferredEndTime, selectedGuestServiceStatus]);

  // reset data
  useEffect(() => {
    if (props.open) {
      setSelectedDeferredEndTime(
        createLocalTimeFromTzTime(
          props.serviceDeferredUntil,
          property.timeZoneCode
        )
      );
      setSelectedGuestServiceStatus(props.selectedGuestService);
    }
  }, [props.serviceDeferredUntil, props.open]);

  return (
    <AcModal className={prefix} isOpen={props.open} onClose={props.onClose}>
      <AcModalHeader showCloseButton={true}>
        {t('GLOBAL.GUEST_SERVICE_STATUS.TITLE')}
      </AcModalHeader>

      <AcModalBody className={`${prefix}-body`}>
        <AcFlex direction={FlexDirection.column} grow={true}>
          {props.description && (
            <div className={`${prefix}-description`}>
              <AcText size={TextSize.Main1}>{props.description}</AcText>
            </div>
          )}
          <ul className={`${prefix}-list`}>
            {enabledGuestServiceStatuses.map(
              (guestService: DictionaryEntity, key: number) => {
                return (
                  <li
                    key={key}
                    className={`${prefix}-list-item`}
                    onClick={() =>
                      onItemClick(guestService.code as GuestServiceStatusType)
                    }
                  >
                    <AcFlex
                      direction={FlexDirection.row}
                      grow={true}
                      alignItems={AlignItems.flexStart}
                    >
                      <AcRadioButton
                        name="guest-service"
                        className={`${prefix}-radio`}
                        testSelector={formatTestSelector(
                          prefix,
                          guestService.name
                            ?.toLocaleLowerCase()
                            .replace(' ', '-'),
                          'radio'
                        )}
                        checked={
                          guestService.code === selectedGuestServiceStatus
                        }
                      />
                      <div className={`${prefix}-item-name`}>
                        <AcText size={TextSize.Main1}>
                          {guestService.name}
                        </AcText>

                        {guestService.code ===
                          GuestServiceStatusType.ServiceDeferred && (
                          <AcFormElement
                            className="ac-spacing-top-s"
                            label={t(
                              'GLOBAL.GUEST_SERVICE_STATUS.PREFERRED_TIME_OPTIONAL'
                            )}
                          >
                            <AcFieldTime
                              format={formats.time}
                              onChange={setSelectedDeferredEndTime}
                              value={selectedDeferredEndTime}
                              onFocus={onFocus}
                              testSelector={formatTestSelector(
                                prefix,
                                'time-input'
                              )}
                              validation={
                                !isServiceDeferredUntilValid
                                  ? t(
                                      'GLOBAL.GUEST_SERVICE_STATUS.IN_THE_PAST_ERROR'
                                    )
                                  : ''
                              }
                            />
                          </AcFormElement>
                        )}
                      </div>
                    </AcFlex>
                  </li>
                );
              }
            )}
          </ul>
        </AcFlex>
      </AcModalBody>

      <AcModalFooter>
        <AcFlex
          direction={FlexDirection.column}
          grow={true}
          justifyContent={JustifyContent.center}
          alignItems={AlignItems.center}
        >
          <AcButton
            onClick={onConfirm}
            fullwidth={true}
            disabled={
              !selectedGuestServiceStatus || !isServiceDeferredUntilValid
            }
          >
            <AcButtonContent text={t('GLOBAL.CONFIRM')} />
          </AcButton>
          <AcButton
            fullwidth={true}
            pattern={AcButtonPattern.Tertiary}
            onClick={props.onClose}
          >
            <AcButtonContent icon={Icon.Cancel} text={t('GLOBAL.CANCEL')} />
          </AcButton>
        </AcFlex>
      </AcModalFooter>
    </AcModal>
  );
};

export default memo(GuestServiceModal);



// WEBPACK FOOTER //
// ./src/components/shared/GuestService/GuestServiceModal.tsx